// One event-stream row and the list model shared by the document stream and the live tail (DESIGN_SPEC §10.9).
//   <StreamItemView item live? terms? onToggle onDetail />   a row (.ev) or its inline detail sheet
//     row: T0 / UTC gutter (the prefix repeated from the row above is faint) · kind as a tinted CategoryChip (the
//     category mark alone on phones) · body parts as text nodes (process, pid faint, connective text mute) with search
//     matches marked · the cited severity and technique (documents only, a 2px edge) · ×N. Folded rows expand to
//     their events (Enter or click); a single event opens its detail (seq, source, times, fields, copy JSON).
//     A focused row: ↑ ↓ move, c copies its text in full, Shift+C its events as JSON (one per line when folded).
//     The stream is one Tab stop (a roving tabindex: the first row, then the row last focused); a row is a button
//     with aria-expanded (its group or its detail sheet), its own +/− toggle and technique link stay out of Tab.
//   <EvHead clock className? />                                 the column header
//   buildItems(rows, opts) → StreamItem[]                        rows + expanded children + open details, flat
//   parseQuery("pid:4821 akamai") · matchRow(row, query) · timeLabel(row, clock) · repeatLen(prev, cur)
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { CatMark, CategoryChip, CopyText, Sev, Sheet } from "@/components/ui";
import { copyText } from "@/lib/clipboard";
import { rel, times, utc } from "@/lib/format";
import type { BodyPart } from "@/lib/derive/types";
import type { Event } from "@/types";
import type { StreamRow as Row } from "../derive/fold";
import { techniqueHref } from "../derive/attack";

export type Clock = "t0" | "utc";

export type StreamItem =
  | { type: "row"; key: string; row: Row; child: boolean; expandable: boolean; open: boolean; detail: boolean; time: string; rep: number; json: () => string; first: boolean }
  | { type: "detail"; key: string; row: Row; event?: Event };

/** A DOM id for an item's detail sheet (keys hold "/" and "#"). */
const detailId = (key: string) => `evd-${key.replace(/[^A-Za-z0-9_-]/g, "_")}`;

type Query = { terms: string[]; pids: number[]; text: string };

export function parseQuery(q: string): Query {
  const terms: string[] = [];
  const pids: number[] = [];
  for (const tok of q.trim().split(/\s+/).filter(Boolean)) {
    const m = /^pid:(\d+)$/i.exec(tok);
    if (m) pids.push(Number(m[1]));
    else terms.push(tok.toLowerCase());
  }
  return { terms, pids, text: q.trim() };
}

export const matchRow = (row: Row, q: Query): boolean => q.pids.every((p) => row.pids.includes(p)) && q.terms.every((t) => row.hay.includes(t));

const timeLabel = (row: Pick<Row, "t" | "at">, clock: Clock): string => (clock === "utc" ? utc(row.at, "time-ms") : rel(row.t));

/** Length of the prefix `cur` repeats from `prev`, cut at a ":" or "." boundary. */
function repeatLen(prev: string | undefined, cur: string): number {
  if (!prev) return 0;
  let cut = 0;
  for (let i = 0; i < prev.length && i < cur.length && prev[i] === cur[i]; i++) if (cur[i] === ":" || cur[i] === ".") cut = i + 1;
  return cut;
}

export function buildItems(
  rows: readonly Row[],
  opts: { clock: Clock; expanded: ReadonlySet<string>; details: ReadonlySet<string>; child: (seq: number) => Row | undefined; event: (seq: number) => Event | undefined },
): StreamItem[] {
  const out: StreamItem[] = [];
  let prev: string | undefined;
  const json = (row: Row) => () =>
    row.seqs
      .map((s) => opts.event(s))
      .filter((e): e is Event => !!e)
      .map((e) => JSON.stringify(e))
      .join("\n");
  const push = (row: Row, key: string, child: boolean) => {
    const expandable = !child && (row.children?.length ?? 0) > 1;
    const time = timeLabel(row, opts.clock);
    const open = expandable && opts.expanded.has(key);
    const detail = !expandable && opts.details.has(key);
    out.push({ type: "row", key, row, child, expandable, open, detail, time, rep: repeatLen(prev, time), json: json(row), first: out.length === 0 });
    prev = time;
    if (detail) out.push({ type: "detail", key: `${key}#d`, row, event: opts.event(row.seq) });
    return open;
  };
  for (const row of rows) {
    if (!push(row, row.key, false)) continue;
    for (const seq of row.children ?? []) {
      const c = opts.child(seq);
      if (c) push(c, `${row.key}/${seq}`, true);
    }
  }
  return out;
}

// ---- text with search matches marked (text nodes only; never HTML) ------------------------------

function Marked({ text, terms }: { text: string; terms?: readonly string[] }) {
  if (!terms?.length || !text) return <>{text}</>;
  const low = text.toLowerCase();
  const spans: [number, number][] = [];
  for (const t of terms) {
    if (!t) continue;
    for (let i = low.indexOf(t); i >= 0; i = low.indexOf(t, i + t.length)) spans.push([i, i + t.length]);
  }
  if (!spans.length) return <>{text}</>;
  spans.sort((a, b) => a[0] - b[0]);
  const out: ReactNode[] = [];
  let at = 0;
  for (const [a, b] of spans) {
    if (b <= at) continue;
    const s = Math.max(a, at);
    if (s > at) out.push(text.slice(at, s));
    out.push(
      <mark key={s} className="bg-cta/45 text-ink">
        {text.slice(s, b)}
      </mark>,
    );
    at = b;
  }
  if (at < text.length) out.push(text.slice(at));
  return <>{out}</>;
}

const TONE: Record<NonNullable<BodyPart["tone"]> | "ink", string | undefined> = { ink: undefined, soft: "text-ink-soft", mute: "m", faint: "f" };

function Parts({ parts, terms }: { parts: readonly BodyPart[]; terms?: readonly string[] }) {
  return (
    <>
      {parts.map((p, i) => (
        <span key={i} className={TONE[p.tone ?? "ink"]}>
          <Marked text={p.text} terms={terms} />
        </span>
      ))}
    </>
  );
}

// ---- the column header and a row ---------------------------------------------------------------------

/** Above 720px the kind chip carries the category; below, the kind column hides and the mark stays. */
const GRID = "min-[721px]:grid-cols-[11.5ch_18ch_minmax(0,1fr)_7ch]";

export function EvHead({ clock, className }: { clock: Clock; className?: string }) {
  return (
    <div className={`ev ev-head ${GRID} ${className ?? ""}`} aria-hidden="true">
      <span>{clock === "utc" ? "utc" : "t − T0"}</span>
      <span className="min-[721px]:hidden" />
      <span>kind</span>
      <span>event</span>
      <span className="text-right">×</span>
    </div>
  );
}

function moveFocus(from: HTMLElement, dir: 1 | -1) {
  const all = Array.from(from.closest("[role=log]")?.querySelectorAll<HTMLElement>(".ev[tabindex]") ?? []);
  all[all.indexOf(from) + dir]?.focus();
}

/** Roving tab stop: the focused row becomes the stream's one Tab stop. */
function takeTabStop(row: HTMLElement) {
  row.closest("[role=log]")
    ?.querySelectorAll<HTMLElement>('.ev[tabindex="0"]')
    .forEach((el) => {
      if (el !== row) el.tabIndex = -1;
    });
  row.tabIndex = 0;
}

export function StreamItemView({
  item,
  live,
  terms,
  onToggle,
  onDetail,
}: {
  item: StreamItem;
  live?: boolean;
  terms?: readonly string[];
  onToggle: (key: string) => void;
  onDetail: (key: string) => void;
}) {
  if (item.type === "detail") return <RowDetail id={detailId(item.key.slice(0, -2))} row={item.row} event={item.event} />;
  return <RowView item={item} live={live} terms={terms} onToggle={onToggle} onDetail={onDetail} />;
}

function RowView({
  item,
  live,
  terms,
  onToggle,
  onDetail,
}: {
  item: Extract<StreamItem, { type: "row" }>;
  live?: boolean;
  terms?: readonly string[];
  onToggle: (key: string) => void;
  onDetail: (key: string) => void;
}) {
  const { row } = item;
  const sev = !live && row.sev ? row.sev : undefined;
  const [copied, setCopied] = useState<string | null>(null);
  const timer = useRef(0);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const copy = async (asJson: boolean) => {
    const ok = await copyText(asJson ? item.json() : row.full);
    window.clearTimeout(timer.current);
    setCopied(ok ? (asJson ? "Row JSON copied" : "Row copied") : "Couldn't copy the row");
    timer.current = window.setTimeout(() => setCopied(null), 1200);
  };
  const act = () => (item.expandable ? onToggle(item.key) : onDetail(item.key));
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      act();
    } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      moveFocus(e.currentTarget, e.key === "ArrowDown" ? 1 : -1);
    } else if ((e.key === "c" || e.key === "C") && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      void copy(e.shiftKey);
    }
  };
  const classes = ["ev", GRID, row.pre && "is-pre", item.expandable && "is-group", item.child && "is-child", row.background && "text-ink-soft", "cursor-pointer"].filter(Boolean).join(" ");
  return (
    <div
      className={classes}
      data-sev={sev}
      data-open={item.expandable ? item.open : item.detail}
      role="button"
      aria-expanded={item.expandable ? item.open : item.detail}
      aria-controls={!item.expandable && item.detail ? detailId(item.key) : undefined}
      tabIndex={item.first ? 0 : -1}
      onFocus={(e) => e.target === e.currentTarget && takeTabStop(e.currentTarget)}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest("a, button")) return;
        act();
      }}
      onKeyDown={onKeyDown}
    >
      <span className="tm" translate="no">
        {item.rep ? (
          <>
            <i>{item.time.slice(0, item.rep)}</i>
            {item.time.slice(item.rep)}
          </>
        ) : (
          item.time
        )}
      </span>
      <span className="mk min-[721px]:hidden">
        <CatMark c={row.category} />
      </span>
      <span className="kd min-w-0 items-center min-[721px]:flex">
        {item.expandable && (
          <button
            type="button"
            tabIndex={-1}
            aria-hidden="true"
            className="tg flex-none border-0 bg-transparent p-0 text-left font-mono hover:text-ink"
            title={`${item.open ? "Fold" : "Show"} the ${row.children?.length ?? 0} events on this row`}
            onClick={() => onToggle(item.key)}
          >
            {item.open ? "−" : "+"}
          </button>
        )}
        <CategoryChip c={row.category} size="sm" className="min-w-0" title={row.kind}>
          {row.kind}
        </CategoryChip>
      </span>
      <span className="bd">
        <span className="tx" title={row.full} translate="no">
          <Parts parts={row.parts} terms={terms} />
        </span>
        {sev && (
          <span className="sevpair">
            <Sev level={sev} />
            {row.mitre && (
              <a className="tech-id" href={techniqueHref(row.mitre)} target="_blank" rel="noopener noreferrer" translate="no" tabIndex={-1}>
                {row.mitre}
              </a>
            )}
          </span>
        )}
      </span>
      <span className="n">{copied ? <span className="text-ink">{copied.startsWith("Row") ? "copied" : "failed"}</span> : row.count > 1 ? times(row.count) : ""}</span>
      <span className="sr-only" role="status">
        {copied ?? ""}
      </span>
    </div>
  );
}

// ---- the inline detail sheet ------------------------------------------------------------------------

const MAX_VALUE = 600;

function fieldText(v: unknown): string {
  if (v === null || v === undefined) return "";
  const s = typeof v === "object" ? JSON.stringify(v) : String(v);
  return s.length > MAX_VALUE ? `${s.slice(0, MAX_VALUE)}…` : s;
}

function RowDetail({ id, row, event }: { id: string; row: Row; event?: Event }) {
  if (!event) return null;
  const json = JSON.stringify(event);
  const rows = [
    { k: "seq", v: String(event.seq) },
    { k: "kind", v: event.kind },
    { k: "source", v: event.source || "—" },
    {
      k: "time",
      v: (
        <>
          <time dateTime={new Date(row.at).toISOString()}>{utc(row.at, "time-ms")} UTC</time>
          <span className="text-ink-mute"> · {rel(row.t)} from T0</span>
        </>
      ),
    },
    ...Object.entries(event.fields ?? {}).map(([k, v]) => ({ k, v: fieldText(v), key: `f:${k}` })),
  ];
  return (
    <div id={id} className="border-b border-rule-row bg-raised px-2.5 pt-1 pb-3 max-[720px]:px-1.5" role="group" aria-label={`Event ${event.seq}`}>
      <Sheet rows={rows} keyCol="minmax(80px, 16ch)" className="border-t-0" />
      <p className="mt-2.5 flex flex-wrap items-center gap-3 font-mono text-meta text-ink-faint">
        <CopyText value={json} label="event JSON">
          copy JSON
        </CopyText>
        <span>
          Raw event · {json.length.toLocaleString("en-US")} bytes
        </span>
      </p>
    </div>
  );
}

/** A plain list of items (grouped mode renders inline; the scroller renders the same items virtualized). */
export function ItemList({ items, live, terms, onToggle, onDetail }: { items: readonly StreamItem[]; live?: boolean; terms?: readonly string[]; onToggle: (k: string) => void; onDetail: (k: string) => void }) {
  return (
    <>
      {items.map((it) => (
        <StreamItemView key={it.key} item={it} live={live} terms={terms} onToggle={onToggle} onDetail={onDetail} />
      ))}
    </>
  );
}
